// ============================================================================
// DEVICE MANAGER SERVICE
// ============================================================================
import {
  API_BASE_URL,
  DeviceDYNCode,
  DeviceSessionInfo,
  Device,
  DeviceCodesResponse,
  DeviceListResult,
  DeviceResponse,
  DeviceTokenDeleteResult,
  DeviceTokenDeleteSummary,
  DeviceTokenGroup,
  DeviceTokenList,
} from '@types';
import {
  createEmptyPaginatedResult,
  normalizePaginatedResponse,
} from './utils/pagination';
import { isArray } from 'remeda';
// ============================================================================
// ABLY UTILITIES
// ============================================================================

// const getAblyInstance = async (): Promise<any> => {
//   try {
//     // Import Ably dynamically to avoid SSR issues
//     const { default: Ably } = await import('ably');

//     // Get token from localStorage or fetch new one
//     const tokenData = localStorage.getItem('ablyTokenData');
//     if (!tokenData) {
//       console.warn('No Ably token found');
//       return null;
//     }

//     const token = JSON.parse(tokenData);
//     if (Date.now() >= token.expires) {
//       console.warn('Ably token expired');
//       return null;
//     }

//     return new Ably.Realtime({
//       authCallback: (_params: any, callback: any) => {
//         callback(null, token);
//       },
//     });
//   } catch (error) {
//     console.error('Error creating Ably instance:', error);
//     return null;
//   }
// };

// const getDeviceMessageHistory = async (
//   userId: string,
//   deviceCode: string,
//   limit: number = 100
// ): Promise<AblyMessage[]> => {
//   try {
//     const ably = await getAblyInstance();
//     if (!ably) {
//       console.warn('Ably not available for message history');
//       return [];
//     }

//     const channel = ably.channels.get(`private:${userId}`);

//     // Get channel history
//     const history = await channel.history({ limit });

//     // Filter messages by device code
//     const deviceMessages = history.items
//       .filter((message: any) => {
//         try {
//           const data = typeof message.data === 'string'
//             ? JSON.parse(message.data)
//             : message.data;
//           return data.code === deviceCode;
//         } catch {
//           return false;
//         }
//       })
//       .map((message: any) => ({
//         id: message.id!,
//         name: message.name!,
//         data: typeof message.data === 'string' ? JSON.parse(message.data) : message.data,
//         timestamp: message.timestamp!,
//         clientId: message.clientId,
//       }))
//       .sort((a: AblyMessage, b: AblyMessage) => b.timestamp - a.timestamp); // Sort by timestamp descending

//     return deviceMessages;
//   } catch (error) {
//     console.error(`Error getting message history for device ${deviceCode}:`, error);
//     return [];
//   }
// };

// ============================================================================
// API FUNCTIONS
// ============================================================================

export type TimerInfo = {
  timeoutId: ReturnType<typeof setTimeout>;
  expiresAt: number;
};

const EMPTY_DEVICE_LIST: DeviceListResult =
  createEmptyPaginatedResult<Device>();

export const getConnectedDevices = async (): Promise<DeviceResponse> => {
  const response = await fetch(`${API_BASE_URL}/services/userDevices`, {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`HTTP ${response.status}: ${errorText}`);
  }

  return response.json();
};

export const getConnectedDevicesSafe = async (): Promise<DeviceListResult> => {
  try {
    const response = await getConnectedDevices();
    return normalizePaginatedResponse<Device>(response);
  } catch (error) {
    console.error('Error fetching connected devices:', error);
    return EMPTY_DEVICE_LIST;
  }
};

// Get devices for a specific user (admin function)
export const getUserDevices = async (
  userId: string
): Promise<DeviceResponse> => {
  const response = await fetch(
    `${API_BASE_URL}/services/v1/users/${encodeURIComponent(userId)}/devices`,
    {
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    }
  );

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`HTTP ${response.status}: ${errorText}`);
  }

  return response.json();
};

export const getUserDevicesSafe = async (
  userId: string
): Promise<DeviceListResult> => {
  try {
    const response = await getUserDevices(userId);

    return normalizePaginatedResponse<Device>(response);
  } catch (error) {
    console.error('Error fetching user devices:', error);
    return EMPTY_DEVICE_LIST;
  }
};

//   userId: string,
//   messageLimit: number = 50
// ): Promise<DeviceWithHistoryResponse> => {
//   return { data: [] };
//   try {
//     // First get the basic device list
//     const devicesResponse = await getConnectedDevices();
//     const devices = devicesResponse.data || [];

//     // For each device, get message history
//     const devicesWithHistory: DeviceWithHistory[] = await Promise.all(
//       devices.map(async (device) => {
//         const deviceWithHistory: DeviceWithHistory = { ...device };

//         // Only get history if device has a code
//         if (device.deviceCode) {
//           try {
//             const messageHistory = await getDeviceMessageHistory(
//               userId,
//               device.deviceCode,
//               messageLimit
//             );

//             deviceWithHistory.messageHistory = messageHistory;
//             deviceWithHistory.messageCount = messageHistory.length;

//             // Get timestamp of last message
//             if (messageHistory.length > 0) {
//               deviceWithHistory.lastMessageTime = messageHistory[0].timestamp;
//             }
//           } catch (error) {
//             console.error(`Error getting history for device ${device.deviceCode}:`, error);
//             deviceWithHistory.messageHistory = [];
//             deviceWithHistory.messageCount = 0;
//           }
//         } else {
//           deviceWithHistory.messageHistory = [];
//           deviceWithHistory.messageCount = 0;
//         }

//         return deviceWithHistory;
//       })
//     );

//     return { data: devicesWithHistory };
//   } catch (error) {
//     console.error('Error fetching devices with history:', error);
//     throw error;
//   }
// };

// export const getConnectedDevicesWithHistorySafe = async (
//   userId: string,
//   messageLimit: number = 50
// ): Promise<DeviceWithHistory[]> => {
//   try {
//     const response = await getConnectedDevicesWithHistory(userId, messageLimit);
//     return response.data || [];
//   } catch (error) {
//     console.error('Error fetching devices with history:', error);
//     return [];
//   }
// };

const buildUserDeviceUrl = (userId: string, deviceId?: string) => {
  const base = `${API_BASE_URL}/services/v1/users/${encodeURIComponent(
    userId
  )}/devices`;
  return deviceId ? `${base}/${encodeURIComponent(deviceId)}` : base;
};

export const deleteUserDevice = async (
  userId: string,
  deviceId: string
): Promise<Device> => {
  const response = await fetch(buildUserDeviceUrl(userId, deviceId), {
    method: 'DELETE',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
    },
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`HTTP ${response.status}: ${errorText}`);
  }

  const payload = await response.json();
  const data = payload?.data;

  if (!data) {
    throw new Error('Missing device data');
  }

  return data as Device;
};

export const deleteUserDeviceSafe = async (
  userId: string,
  deviceId: string
): Promise<Device | null> => {
  try {
    return await deleteUserDevice(userId, deviceId);
  } catch (error) {
    console.error('Error deleting user device:', error);
    return null;
  }
};

export const createUserDevice = async (
  values: unknown,
  userId: string
): Promise<Device> => {
  const response = await fetch(buildUserDeviceUrl(userId), {
    method: 'POST',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(values),
  });

  if (!response.ok) {
    const errorText = await response.text();
    let apiMessage = errorText;
    try {
      const parsed = JSON.parse(errorText) as { message?: string };
      if (parsed?.message) apiMessage = parsed.message;
    } catch {
      /* keep raw text */
    }
    throw new Error(apiMessage);
  }

  const payload = await response.json();
  const data = (payload?.data ?? payload) as Device | undefined;

  if (!data) {
    throw new Error('Missing device data');
  }

  return data;
};

export const createDevice = async (
  values: unknown,
  userId?: string
): Promise<Device> => {
  if (userId) {
    return createUserDevice(values, userId);
  }

  const response = await fetch(`${API_BASE_URL}/services/userDevices`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(values),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`HTTP ${response.status}: ${errorText}`);
  }

  return response.json();
};

export const createDeviceSafe = async (
  values: unknown,
  userId?: string
): Promise<boolean> => {
  try {
    await createDevice(values, userId);
    return true;
  } catch (error) {
    console.error('Error creating device:', error);
    return false;
  }
};

const buildUserDeviceTokensUrl = (userId: string) =>
  `${API_BASE_URL}/services/v1/users/${encodeURIComponent(
    userId
  )}/devices/tokens`;

export const getDeviceCodes = async (): Promise<DeviceCodesResponse> => {
  const response = await fetch(
    `${API_BASE_URL}/services/userDevices/deviceCodes`,
    {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`HTTP ${response.status}: ${errorText}`);
  }

  return response.json();
};

export const getDeviceCodesSafe =
  async (): Promise<DeviceCodesResponse | null> => {
    try {
      return await getDeviceCodes();
    } catch (error) {
      console.error('Error getting device codes:', error);
      return null;
    }
  };

const ensureString = (
  value: unknown,
  fallback: string | undefined = ''
): string => {
  const resolvedFallback = fallback ?? '';
  if (typeof value === 'string') return value;
  if (value === null || value === undefined) return resolvedFallback;
  return String(value);
};

const ensureNumber = (value: unknown, fallback = 0): number => {
  const num = Number(value);
  return Number.isFinite(num) ? num : fallback;
};

const mapRawDeviceCode = (
  raw: unknown,
  defaults?: Partial<DeviceDYNCode>,
  sessionsLookup?: Map<string, DeviceSessionInfo>
): DeviceDYNCode => {
  const source =
    raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const base = defaults ?? {};

  const resolvedCode = ensureString(source.code, base.code ?? '');
  const resolvedDeviceCode = ensureString(
    source.deviceCode,
    base.deviceCode ?? ''
  );
  const sessionString = ensureString(source.session, base.session ?? '');
  const sessionId = sessionString.split('#')[0] || undefined;

  const primarySession = sessionId ? sessionsLookup?.get(sessionId) : undefined;
  const relatedSessions = (() => {
    if (!sessionsLookup) return primarySession ? [primarySession] : [];
    return Array.from(sessionsLookup.values()).filter(
      session =>
        session.deviceCode === resolvedDeviceCode &&
        (!sessionId || session.sessionId === sessionId)
    );
  })();

  return {
    code: resolvedCode,
    session: sessionString,
    expiresAt: ensureNumber(source.expiresAt, base.expiresAt ?? 0),
    userId: ensureString(source.userId, base.userId ?? ''),
    status: ensureString(source.status, base.status ?? ''),
    deviceCode: resolvedDeviceCode,
    sk: ensureString(source.sk, base.sk ?? ''),
    pk: ensureString(source.pk, base.pk ?? ''),
    deviceToken: ensureString(source.deviceToken, base.deviceToken ?? ''),
    updatedAt:
      source.updatedAt !== undefined
        ? ensureString(source.updatedAt, base.updatedAt)
        : base.updatedAt,
    appId:
      source.appId !== undefined
        ? Number(source.appId)
        : (base.appId ?? primarySession?.appId),
    deviceId:
      source.deviceId !== undefined
        ? ensureString(source.deviceId)
        : (base.deviceId ??
          (base.deviceCode ? ensureString(base.deviceCode) : undefined)),
    sessionInfo: primarySession,
    sessions: relatedSessions,
  };
};

const mapRawDeviceSession = (raw: unknown): DeviceSessionInfo => {
  const source =
    raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};

  const sessionId = ensureString(source.sessionId);
  const appId = source.appId !== undefined ? Number(source.appId) : undefined;
  const createdAt =
    source.createdAt !== undefined ? Number(source.createdAt) : undefined;
  const expiresAt =
    source.expiresAt !== undefined ? Number(source.expiresAt) : undefined;

  return {
    sessionId,
    userAgent: ensureString(source.userAgent),
    ipAddress: ensureString(source.ipAddress),
    deviceCode: ensureString(source.deviceCode),
    appId: Number.isFinite(appId) ? (appId as number) : undefined,
    createdAt: Number.isFinite(createdAt) ? (createdAt as number) : undefined,
    expiresAt: Number.isFinite(expiresAt) ? (expiresAt as number) : undefined,
  };
};

const normalizeDeleteResults = (value: unknown): DeviceTokenDeleteResult[] => {
  const items = isArray(value) ? (value as unknown[]) : [];
  return items.map(item => ({
    id: ensureString((item as any)?.id),
    success: Boolean((item as any)?.success),
    sessionDeleted: (item as any)?.sessionDeleted ?? false,
    sessionId: (item as any)?.sessionId ?? null,
    record: (item as any)?.record
      ? mapRawDeviceCode((item as any).record)
      : null,
    error: (item as any)?.error,
  }));
};

const buildDeviceCodesUrl = (
  userId: string,
  deviceId: string,
  code?: string
) => {
  const base = `${API_BASE_URL}/services/v1/users/${encodeURIComponent(
    userId
  )}/devices/${encodeURIComponent(deviceId)}/codes`;
  return code ? `${base}/${encodeURIComponent(code)}` : base;
};

const buildSingleDeviceTokenUrl = (
  userId: string,
  deviceId: string,
  token: string
) =>
  `${API_BASE_URL}/services/v1/users/${encodeURIComponent(
    userId
  )}/devices/${encodeURIComponent(deviceId)}/tokens/${encodeURIComponent(
    token
  )}`;

export const getUserDeviceTokens = async (
  userId: string,
  deviceId: string
): Promise<DeviceTokenList> => {
  const response = await fetch(buildDeviceCodesUrl(userId, deviceId), {
    method: 'GET',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
    },
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`HTTP ${response.status}: ${errorText}`);
  }

  const payload = await response.json();
  const data = payload?.data ?? {};

  const deviceIdStr = ensureString(data.deviceId, deviceId);
  const userIdStr = ensureString(data.userId, userId);
  const deviceCodeStr = ensureString(data.deviceCode);

  const rawSessions = isArray(data.sessions)
    ? (data.sessions as unknown[])
    : [];
  const sessions = rawSessions.map(mapRawDeviceSession);
  const sessionsById = new Map<string, DeviceSessionInfo>();
  sessions
    .filter(session => session.sessionId)
    .forEach(session => {
      sessionsById.set(session.sessionId, session);
    });

  const rawCodes = isArray(data.codes) ? (data.codes as unknown[]) : [];
  const codes = rawCodes.map(item =>
    mapRawDeviceCode(
      item,
      {
        deviceId: deviceIdStr,
        deviceCode: deviceCodeStr,
        userId: userIdStr,
      },
      sessionsById
    )
  );

  const filteredSessions = sessions.filter(
    session => session.deviceCode && session.deviceCode === deviceCodeStr
  );

  return {
    userId: userIdStr,
    deviceId: deviceIdStr,
    deviceCode: deviceCodeStr,
    codes,
    sessions: filteredSessions,
    count: Number.isFinite(data.count) ? Number(data.count) : codes.length,
  };
};

export const getUserDeviceCodesSafe = async (
  userId: string,
  deviceId: string
): Promise<DeviceTokenList> => {
  try {
    return await getUserDeviceTokens(userId, deviceId);
  } catch (error) {
    console.error('Error fetching user device codes:', error);
    return {
      userId,
      deviceId,
      deviceCode: '',
      codes: [],
      sessions: [],
      count: 0,
    };
  }
};

export const deleteUserDeviceCodes = async (
  userId: string,
  deviceId: string,
  tokens: string[]
): Promise<DeviceTokenDeleteSummary> => {
  if (!isArray(tokens) || tokens.length === 0) {
    throw new Error('Tokens array is required and must not be empty');
  }

  const response = await fetch(buildDeviceCodesUrl(userId, deviceId), {
    method: 'DELETE',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ tokens }),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`HTTP ${response.status}: ${errorText}`);
  }

  const payload = await response.json();
  const data = payload?.data ?? {};

  return {
    total: Number.isFinite(data.total) ? Number(data.total) : 0,
    success: Number.isFinite(data.success) ? Number(data.success) : 0,
    failed: Number.isFinite(data.failed) ? Number(data.failed) : 0,
    results: normalizeDeleteResults(data.results).map(result => ({
      ...result,
      record: result.record
        ? {
            ...result.record,
            deviceId:
              result.record.deviceId ?? ensureString(data.deviceId, deviceId),
            userId: result.record.userId ?? ensureString(data.userId, userId),
          }
        : null,
    })),
  };
};

export const deleteUserDeviceCodesSafe = async (
  userId: string,
  deviceId: string,
  tokens: string[]
): Promise<DeviceTokenDeleteSummary> => {
  try {
    return await deleteUserDeviceCodes(userId, deviceId, tokens);
  } catch (error) {
    console.error('Error deleting user device codes:', error);
    return {
      total: 0,
      success: 0,
      failed: 0,
      results: [],
    };
  }
};

export const deleteSingleUserDeviceCode = async (
  userId: string,
  deviceId: string,
  token: string
): Promise<DeviceTokenDeleteSummary> => {
  const response = await fetch(
    buildSingleDeviceTokenUrl(userId, deviceId, token),
    {
      method: 'DELETE',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
      },
    }
  );

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`HTTP ${response.status}: ${errorText}`);
  }

  const payload = await response.json();
  const data = payload?.data ?? {};

  return {
    total: Number.isFinite(data.total) ? Number(data.total) : 1,
    success: Number.isFinite(data.success)
      ? Number(data.success)
      : response.ok
        ? 1
        : 0,
    failed: Number.isFinite(data.failed)
      ? Number(data.failed)
      : response.ok
        ? 0
        : 1,
    results: normalizeDeleteResults(data.results ?? [data]).map(result => ({
      ...result,
      record: result.record
        ? {
            ...result.record,
            deviceId:
              result.record.deviceId ?? ensureString(data.deviceId, deviceId),
            userId: result.record.userId ?? ensureString(data.userId, userId),
          }
        : null,
    })),
  };
};

export const deleteSingleUserDeviceCodeSafe = async (
  userId: string,
  deviceId: string,
  code: string
): Promise<DeviceTokenDeleteSummary> => {
  try {
    return await deleteSingleUserDeviceCode(userId, deviceId, code);
  } catch (error) {
    console.error('Error deleting user device code:', error);
    return {
      total: 0,
      success: 0,
      failed: 0,
      results: [],
    };
  }
};

// Alias for deleteSingleUserDeviceCode to match the API naming
export const deleteSingleUserDeviceToken = deleteSingleUserDeviceCode;

export const deleteSingleUserDeviceTokenSafe = async (
  userId: string,
  deviceId: string,
  token: string
): Promise<DeviceTokenDeleteSummary> => {
  try {
    return await deleteSingleUserDeviceCode(userId, deviceId, token);
  } catch (error) {
    console.error('Error deleting user device token:', error);
    return {
      total: 0,
      success: 0,
      failed: 0,
      results: [],
    };
  }
};

const EMPTY_DEVICE_TOKEN_GROUPS: DeviceTokenGroup[] = [];

export const getAllUserDeviceTokens = async (
  userId: string
): Promise<DeviceTokenGroup[]> => {
  const response = await fetch(buildUserDeviceTokensUrl(userId), {
    method: 'GET',
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`HTTP ${response.status}: ${errorText}`);
  }

  const payload = await response.json();
  const rawCodes = isArray(payload?.data?.codes)
    ? (payload.data.codes as unknown[])
    : [];
  const rawSessions = isArray(payload?.data?.sessions)
    ? (payload.data.sessions as unknown[])
    : [];

  const normalizedSessions = rawSessions
    .map(mapRawDeviceSession)
    .filter(session => Boolean(session.deviceCode));

  const sessionsById = new Map<string, DeviceSessionInfo>();
  normalizedSessions
    .filter(session => session.sessionId)
    .forEach(session => {
      sessionsById.set(session.sessionId!, session);
    });

  const sessionsByDeviceCode = new Map<string, DeviceSessionInfo[]>();
  normalizedSessions.forEach(session => {
    if (!session.deviceCode) return;
    const existing = sessionsByDeviceCode.get(session.deviceCode) ?? [];
    existing.push(session);
    sessionsByDeviceCode.set(session.deviceCode, existing);
  });
  sessionsByDeviceCode.forEach(list => {
    list.sort((a, b) => (b.expiresAt ?? 0) - (a.expiresAt ?? 0));
  });

  const normalizedCodes = rawCodes.map(code =>
    mapRawDeviceCode(
      code,
      {
        userId,
      },
      sessionsById
    )
  );

  const groups = new Map<string, DeviceTokenGroup>();
  normalizedCodes.forEach(token => {
    const groupKey = token.deviceId ?? token.deviceCode ?? token.userId;
    if (!groupKey || !token.deviceCode) {
      return;
    }

    const existing = groups.get(groupKey);
    if (existing) {
      existing.codes.push(token);
      existing.count = existing.codes.length;
    } else {
      groups.set(groupKey, {
        userId: token.userId,
        deviceId: token.deviceId ?? '',
        deviceCode: token.deviceCode,
        codes: [token],
        sessions: sessionsByDeviceCode.get(token.deviceCode) ?? [],
        count: 1,
      });
    }
  });

  return Array.from(groups.values());
};

export const getAllUserDeviceTokensSafe = async (
  userId: string
): Promise<DeviceTokenGroup[]> => {
  try {
    return await getAllUserDeviceTokens(userId);
  } catch (error) {
    console.error('Error fetching all user device codes:', error);
    return EMPTY_DEVICE_TOKEN_GROUPS;
  }
};

// Device access confirmation
export const confirmDeviceAccess = async (
  code: string,
  deviceCode: string,
  userId?: string
): Promise<Device> => {
  const response = await fetch(`${API_BASE_URL}/auth/device/confirm`, {
    method: 'POST',
    body: JSON.stringify({
      code,
      deviceCode,
      userId,
    }),
    credentials: 'include',
    headers: { 'Content-type': 'application/json; charset=UTF-8' },
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`HTTP ${response.status}: ${errorText}`);
  }

  return response.json() as Promise<Device>;
};

export const confirmDeviceAccessSafe = async (
  code: string,
  deviceCode: string,
  userId: string
): Promise<Device | null> => {
  try {
    return await confirmDeviceAccess(code, deviceCode, userId);
  } catch (error) {
    console.error('Error confirming device access:', error);
    throw error; // Rilanciamo l'errore per gestirlo nel componente
  }
};

// Pair sensor device to VR headset
export const pairDevice = async (
  userId: string,
  deviceId: string,
  values: { connected_to: number | string }
): Promise<Device> => {
  // Validazione
  if (!values.connected_to) {
    throw new Error('connected_to is required for pairing');
  }

  const body = { connected_to: values.connected_to };

  const response = await fetch(
    `${API_BASE_URL}/services/v1/users/${encodeURIComponent(userId)}/devices/${encodeURIComponent(deviceId)}`,
    {
      method: 'PATCH',
      body: JSON.stringify(body),
      credentials: 'include',
      headers: {
        'Content-type': 'application/json; charset=UTF-8',
      },
    }
  );

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`HTTP ${response.status}: ${errorText}`);
  }

  return response.json() as Promise<Device>;
};

export const pairDeviceSafe = async (
  userId: string,
  deviceId: string,
  values: { connected_to: number | string }
): Promise<Device | null> => {
  try {
    return await pairDevice(userId, deviceId, values);
  } catch (error) {
    console.error('Error pairing device:', error);
    throw error;
  }
};

// Unpair sensor device from VR headset
export const unpairDevice = async (
  userId: string,
  deviceId: string
): Promise<Device> => {
  const body = { connected_to: null };

  const response = await fetch(
    `${API_BASE_URL}/services/v1/users/${encodeURIComponent(userId)}/devices/${encodeURIComponent(deviceId)}`,
    {
      method: 'PATCH',
      body: JSON.stringify(body),
      credentials: 'include',
      headers: {
        'Content-type': 'application/json; charset=UTF-8',
      },
    }
  );

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`HTTP ${response.status}: ${errorText}`);
  }

  return response.json() as Promise<Device>;
};

export const unpairDeviceSafe = async (
  userId: string,
  deviceId: string
): Promise<Device | null> => {
  try {
    return await unpairDevice(userId, deviceId);
  } catch (error) {
    console.error('Error unpairing device:', error);
    throw error;
  }
};
